import { h, Fragment } from 'preact';
import { useState } from 'preact/hooks';
import styles from './ProductCard.module.scss';
import { useSwatchMap } from '../../hooks/useSwatchMap';
import AppConfig from '../../config/config';
export function ProductCard({ product, layout = 'gridFour', }) {
    var _a, _b, _c;
    const { title, price, image, metafields, variants } = product;
    const { getSwatchByDisplayName } = useSwatchMap();
    const isImageObj = (img) => typeof img === 'object' && 'url' in img;
    const originalImage = isImageObj(image) ? image.url : image;
    // State for managing the selected variant and current displayed image
    const [selectedVariantId, setSelectedVariantId] = useState(null);
    const [currentImage, setCurrentImage] = useState(originalImage);
    const displayImage = AppConfig.enableColourSwatch ? currentImage : (isImageObj(image) ? image.url : image);
    const imgAlt = isImageObj(image) ? image.alt : title;
    const hasSpecial = price &&
        'special' in price &&
        price.special !== undefined &&
        price.special < ((_b = (_a = price.sale) !== null && _a !== void 0 ? _a : price.regular) !== null && _b !== void 0 ? _b : price.regular);
    const isOnSale = price && price.sale !== undefined && price.sale < price.regular;
    // Extract custom label metafields (extracts first element from array)
    const getMetafieldValue = (key) => {
        const metafield = metafields === null || metafields === void 0 ? void 0 : metafields.find((m) => m.namespace === 'custom' && m.key === key);
        if (!(metafield === null || metafield === void 0 ? void 0 : metafield.value))
            return undefined;
        // Handle JSON-stringified array values (e.g., "[\"New\"]")
        try {
            const parsed = JSON.parse(metafield.value);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed[0];
            }
            return metafield.value;
        }
        catch (_a) {
            // If parsing fails, return the raw value
            return metafield.value;
        }
    };
    // Get full array from metafield (for product_features)
    const getMetafieldArray = (key) => {
        const metafield = metafields === null || metafields === void 0 ? void 0 : metafields.find((m) => m.namespace === 'custom' && m.key === key);
        if (!(metafield === null || metafield === void 0 ? void 0 : metafield.value))
            return [];
        try {
            const parsed = JSON.parse(metafield.value);
            if (Array.isArray(parsed)) {
                return parsed;
            }
            return [];
        }
        catch (_a) {
            return [];
        }
    };
    const customLabelText = getMetafieldValue('label_text');
    const customLabelBgColor = getMetafieldValue('label_background_color');
    const customLabelTextColor = getMetafieldValue('label_text_color');
    const warmthRatingValue = getMetafieldValue('warmth_rating');
    const hasCustomLabel = customLabelText && customLabelBgColor && customLabelTextColor;
    const productFeatures = getMetafieldArray('product_features');
    // Parse warmth rating (1-5 scale: 1=cool/summer, 5=warm/winter)
    const warmthLevel = warmthRatingValue
        ? parseFloat(warmthRatingValue)
        : undefined;
    // Generate gradient based on warmth level
    // Creates a peak of brown color at the position with beige-to-gray fade on both sides
    // 1 = far left (Summer), 3 = center (All Seasons), 5 = far right (Winter)
    const getWarmthGradient = (level) => {
        const position = ((level - 1) / 4) * 100; // 0% to 100%
        const spread = 25; // Width of the peak in percentage points
        return `linear-gradient(to right,
      #EEE1DA 0%,
      #D9D9D9 calc(${position}% - ${spread}%),
      #BA4F27 ${position}%,
      #D9D9D9 calc(${position}% + ${spread}%),
      #EEE1DA 100%)`;
    };
    const warmthGradient = warmthLevel
        ? getWarmthGradient(warmthLevel)
        : undefined;
    const getColorVariants = () => {
        if (!variants || variants.length === 0) {
            return [];
        }
        const colorVariants = [];
        const seenColors = new Set();
        variants.forEach((variant) => {
            var _a, _b;
            if (!variant.merged_options) {
                return;
            }
            // Look for "colour:" in merged_options
            const colorOption = variant.merged_options.find((option) => option.toLowerCase().includes('colour:') ||
                option.toLowerCase().includes('color:'));
            if (colorOption) {
                // Extract the color name after "colour:" or "color:"
                const colorName = (_a = colorOption.split(':')[1]) === null || _a === void 0 ? void 0 : _a.trim();
                if (colorName && !seenColors.has(colorName)) {
                    seenColors.add(colorName);
                    // Get variant image if available
                    const variantImage = typeof variant.image === 'string'
                        ? variant.image
                        : (_b = variant.image) === null || _b === void 0 ? void 0 : _b.url;
                    colorVariants.push({
                        name: colorName,
                        image: variantImage,
                        variantId: variant.id,
                    });
                }
            }
        });
        return colorVariants;
    };
    const colorVariants = getColorVariants();
    const getReviewsIoRatingSku = () => {
        var _a, _b, _c;
        const identifiers = [];
        const originalProductId = (_a = product.customAttributes) === null || _a === void 0 ? void 0 : _a.reviewsIoProductId;
        const reviewVariants = (_c = (_b = product.customAttributes) === null || _b === void 0 ? void 0 : _b.reviewsIoVariants) !== null && _c !== void 0 ? _c : variants;
        identifiers.push(originalProductId);
        identifiers.push(String(product.id).split('-')[0]);
        identifiers.push(product.handle);
        identifiers.push(product.sku);
        reviewVariants === null || reviewVariants === void 0 ? void 0 : reviewVariants.forEach((variant) => {
            identifiers.push(variant.sku);
            identifiers.push(variant.id);
        });
        return Array.from(new Set(identifiers
            .filter((identifier) => identifier !== undefined && identifier !== '')
            .map((identifier) => String(identifier)))).join(';');
    };
    // QUICK ADD FEATURE - Commented out for now
    // // Get size variants for a specific color
    // interface SizeVariant {
    //   size: string;
    //   variantId: string;
    //   available: boolean;
    //   inventory: number;
    // }
    // const getSizeVariantsForColor = (colorName: string): SizeVariant[] => {
    //   if (!variants || variants.length === 0) {
    //     return [];
    //   }
    //   const sizeVariants: SizeVariant[] = [];
    //   variants.forEach((variant) => {
    //     if (!variant.merged_options) {
    //       return;
    //     }
    //     // Check if this variant has the specified color
    //     const colorOption = variant.merged_options.find(
    //       (option) =>
    //         option.toLowerCase().includes('colour:') ||
    //         option.toLowerCase().includes('color:')
    //     );
    //     if (colorOption) {
    //       const variantColor = colorOption.split(':')[1]?.trim();
    //       if (variantColor === colorName) {
    //         // Extract the size from merged_options
    //         const sizeOption = variant.merged_options.find(
    //           (option) => option.toLowerCase().includes('size:')
    //         );
    //         if (sizeOption) {
    //           const size = sizeOption.split(':')[1]?.trim();
    //           if (size) {
    //             sizeVariants.push({
    //               size,
    //               variantId: variant.id,
    //               available: variant.available ?? false,
    //               inventory: variant.inventoryQuantity ?? 0,
    //             });
    //           }
    //         }
    //       }
    //     }
    //   });
    //   return sizeVariants;
    // };
    // // Get size variants for the currently hovered color
    // const hoveredSizeVariants = hoveredColor
    //   ? getSizeVariantsForColor(hoveredColor)
    //   : [];
    // // Split sizes into two rows for pyramid layout
    // const splitSizesIntoRows = (sizes: SizeVariant[]) => {
    //   const midpoint = Math.ceil(sizes.length / 2);
    //   return {
    //     topRow: sizes.slice(0, midpoint),
    //     bottomRow: sizes.slice(midpoint),
    //   };
    // };
    // const sizeRows = splitSizesIntoRows(hoveredSizeVariants);
    // Handler for swatch click - select the variant permanently
    const handleSwatchClick = (colorVariant) => {
        setSelectedVariantId(colorVariant.variantId);
        if (colorVariant.image) {
            setCurrentImage(colorVariant.image);
        }
    };
    // QUICK ADD FEATURE - Commented out for now
    // // Handler for swatch hover - preview the variant and show size overlay
    // const handleSwatchHover = (colorVariant: ColorVariant) => {
    //   setHoveredColor(colorVariant.name);
    //   if (colorVariant.image) {
    //     setCurrentImage(colorVariant.image);
    //     setSelectedVariantId(colorVariant.variantId);
    //   }
    // };
    // // Handler for leaving entire product card - hide overlay
    // const handleCardLeave = () => {
    //   setHoveredColor(null);
    // };
    // // Handler for adding to cart
    // const handleAddToCart = (variantId: string, e: MouseEvent) => {
    //   e.preventDefault();
    //   e.stopPropagation();
    //   if (typeof window !== 'undefined' && (window as any).addToCart) {
    //     (window as any).addToCart(variantId, 1);
    //   }
    // };
    // Get the product URL with optional variant query string
    const getProductUrl = () => {
        var _a;
        const baseUrl = `/products/${product.handle}`;
        // Only append variant if we're in individual colour card mode (not swatch mode)
        // and the product has a colour variant (product.color is set by the expander)
        if (!AppConfig.enableColourSwatch && product.color && ((_a = product.selectedVariant) === null || _a === void 0 ? void 0 : _a.id)) {
            return `${baseUrl}?variant=${product.selectedVariant.id}`;
        }
        // Fallback: if we have a colour but no selectedVariant, use the first variant
        if (!AppConfig.enableColourSwatch && product.color && product.variants && product.variants.length > 0 && product.variants[0].id) {
            return `${baseUrl}?variant=${product.variants[0].id}`;
        }
        // Products without colour variants use base URL
        return baseUrl;
    };
    // Handler for card click - ensure proper navigation
    const handleCardClick = (e) => {
        // Only navigate if not clicking on size buttons or color swatches
        const target = e.target;
        if (target.closest(`.${styles.sizeButton}`) ||
            target.closest(`.${styles.colorSwatch}`)) {
            e.preventDefault();
            return;
        }
        // Ensure we navigate to the correct product URL with variant
        const productUrl = getProductUrl();
        e.preventDefault();
        window.location.href = productUrl;
    };
    return (h("a", { class: styles.productCard, href: getProductUrl(), 
        // onMouseLeave={handleCardLeave} // QUICK ADD FEATURE - Commented out
        onClick: handleCardClick },
        h("div", { class: styles.productImage },
            h("div", { class: styles.badgesContainer },
                isOnSale && h("span", { class: styles.saleBadge }, "Sale"),
                hasCustomLabel && (h("span", { class: styles.customBadge, style: {
                        backgroundColor: customLabelBgColor,
                        color: customLabelTextColor,
                    } }, customLabelText))),
            h("img", { src: displayImage, alt: imgAlt, loading: "lazy" })),
        h("div", { class: styles.cardHeader },
            h("div", { class: styles.headerCenter },
                h("div", { class: styles.reviewsContainer },
                    h("div", { class: "ruk_rating_snippet", "data-sku": getReviewsIoRatingSku() })),
                h("h3", { class: styles.productTitle }, title),
                h("div", { class: styles.productPrice }, hasSpecial ? (h(Fragment, null,
                    h("span", { class: `${styles.priceLabel} ${styles.priceLabelSale}` }, "From "),
                    h("span", { class: styles.salePrice },
                        "$",
                        price.special),
                    h("span", { class: styles.regularPrice },
                        "$", (_c = price.sale) !== null && _c !== void 0 ? _c : price.regular))) : isOnSale ? (h(Fragment, null,
                    h("span", { class: `${styles.priceLabel} ${styles.priceLabelSale}` }, "From "),
                    h("span", { class: styles.salePrice },
                        "$",
                        price.sale),
                    h("span", { class: styles.regularPrice },
                        "$",
                        price.regular))) : (h(Fragment, null,
                    h("span", { class: styles.priceLabel }, "From "),
                    h("span", { class: styles.currentPrice },
                        "$",
                        price.regular)))),
                AppConfig.enableColourSwatch && colorVariants.length > 0 ? (h("div", { class: styles.colorSwatches }, colorVariants.map((colorVariant) => {
                    // Get swatch data from metaobject map
                    const swatchData = getSwatchByDisplayName(colorVariant.name);
                    return (h("button", { key: colorVariant.variantId, class: `${styles.colorSwatch} ${selectedVariantId === colorVariant.variantId
                            ? styles.colorSwatchSelected
                            : ''}`, style: (swatchData === null || swatchData === void 0 ? void 0 : swatchData.colourCode)
                            ? { backgroundColor: swatchData.colourCode }
                            : undefined, "aria-label": colorVariant.name, title: colorVariant.name, onClick: () => handleSwatchClick(colorVariant) }));
                }))) : null))));
}
